<template>
  <AbstractIdeaTaskModalComponent :action="action" :event="event" :disabled="disabled">
    <template #description>
      <EventActionFieldRowComponent :label="t('components.TaskModal.idea.recommend')">
        <div class="flex flex-wrap justify-content-between gap-2">
          <div class="flex-1">
            <LISAButton
              class="w-100"
              :class="
                meta?.recommend === IdeaRecommend.recommend
                  ? 'p-button-success'
                  : 'p-button-success p-button-outlined'
              "
              :disabled="buttonsDisabled"
              @click="setRecommend(IdeaRecommend.recommend)"
            >
              <i class="fas fa-check mr-1" />
              {{ t("components.TaskModal.idea.recommend") }}
            </LISAButton>
          </div>

          <div class="flex-1">
            <LISAButton
              class="w-100"
              :class="
                meta?.recommend === IdeaRecommend.recommendNotPossible
                  ? 'p-button-primary'
                  : 'p-button-primary p-button-outlined'
              "
              :disabled="buttonsDisabled"
              @click="setRecommend(IdeaRecommend.recommendNotPossible)"
            >
              <i class="fas fa-check mr-1" />
              {{ t("components.TaskModal.idea.recommendNotPossible") }}
            </LISAButton>
          </div>

          <div class="flex-1">
            <LISAButton
              class="w-100"
              :class="
                meta?.recommend === IdeaRecommend.recommendNot
                  ? 'p-button-danger'
                  : 'p-button-danger p-button-outlined'
              "
              :disabled="buttonsDisabled"
              @click="setRecommend(IdeaRecommend.recommendNot)"
            >
              <i class="fas fa-times mr-1" />
              {{ t("components.TaskModal.idea.recommendNot") }}
            </LISAButton>
          </div>
        </div>
      </EventActionFieldRowComponent>
    </template>
    <slot />
  </AbstractIdeaTaskModalComponent>
</template>

<script lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, defineComponent, type PropType, ref, watch } from "vue";
import LISAButton from "@LISAPrime/LISAButton.vue";
import EventActionFieldRowComponent from "@/components/event-action/EventActionFieldRowComponent.vue";
import { IdeaRecommend, TaskStatus, type EventListEventFields_HLPIdea_Fragment } from "@/graphql";
import AbstractIdeaTaskModalComponent from "./AbstractIdeaTaskModalComponent.vue";
import injectAsserted from "@/functions/assert/injectAsserted";
import { TaskModalCommentIsRequiredInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import type { EditTaskType } from "@/components/event-action/task/helper/DefaultTask";
import type { EventEditType } from "@/components/event-action/event/modal/EventEditType";

export default defineComponent({
  name: "ManageTaskModalComponent",
  components: {
    AbstractIdeaTaskModalComponent,
    LISAButton,
    EventActionFieldRowComponent,
  },
  props: {
    action: {
      type: Object as PropType<EditTaskType>,
      required: true,
    },
    event: {
      type: Object as PropType<EventListEventFields_HLPIdea_Fragment | EventEditType>,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const { t } = useTranslation();
    const originalMeta = ref({ ...props.action.meta } as {
      recommend: IdeaRecommend;
    });
    function setRecommend(recommend: IdeaRecommend) {
      if (props.action != null) {
        props.action.meta = { recommend };
        props.action.status = TaskStatus.act;
      }
    }

    const buttonsDisabled = computed(
      () =>
        originalMeta.value.recommend === IdeaRecommend.recommendNotPossible ||
        originalMeta.value.recommend === IdeaRecommend.recommendNot ||
        props.disabled,
    );

    const commentIsRequired = injectAsserted(TaskModalCommentIsRequiredInjectionKey);

    const meta = computed(
      () =>
        props.action.meta as {
          recommend: IdeaRecommend;
        },
    );

    const isCommentRequired = computed(
      () =>
        !buttonsDisabled.value &&
        [IdeaRecommend.recommendNot, IdeaRecommend.recommendNotPossible].includes(
          meta.value.recommend,
        ),
    );

    watch(isCommentRequired, (require) => {
      commentIsRequired.value = require;
    });

    return {
      t,
      meta,
      buttonsDisabled,
      originalMeta,
      IdeaRecommend,
      setRecommend,
    };
  },
});
</script>

<style lang="scss" scoped></style>
